import * as O from 'fp-ts/lib/Option';
import { Filter } from './Filter';
import {
    BooleanFilter_Config,
    BooleanFilter_ControlsState,
    BooleanFilter_RequestParams,
    BooleanFilter_Snapshot,
    BooleanFilter_State,
    BooleanFilterAggregation,
    UpdateBooleanFilterPayload_ControlsState,
} from './types/booleanFilter.type';
import {
    Filter_Config,
    Filter_ControlsState,
    Filter_Snapshot,
    Filter_State,
    FilterAggregation,
} from './types/filters.type';
import { FilterType } from './types/filterType.type';

export class BooleanFilter implements Filter {
    private type: FilterType;

    constructor() {
        this.type = 'boolean';
    }

    public isBooleanFilterConfig(config: Filter_Config): config is BooleanFilter_Config {
        return config.type === 'boolean';
    }

    public isBooleanFilterState(state: Filter_State): state is BooleanFilter_State {
        return state.type === 'boolean';
    }

    public isBooleanFilterControlsState(
        controlsState: Filter_ControlsState
    ): controlsState is BooleanFilter_ControlsState {
        return controlsState.type === 'boolean';
    }

    public isBooleanFilterSnapshot(snapshot: Filter_Snapshot): snapshot is BooleanFilter_Snapshot {
        return typeof snapshot === 'string';
    }

    public resetControlsState(controlsState: Filter_ControlsState): Filter_ControlsState {
        return controlsState;
    }

    public isBooleanFilterAggregation(aggregation: FilterAggregation): aggregation is BooleanFilterAggregation {
        return (
            Array.isArray(aggregation) &&
            aggregation.every((item) => 'key' in item && 'doc_count' in item && (item.key === 0 || item.key === 1))
        );
    }

    public createStateFromConfig(config: BooleanFilter_Config): BooleanFilter_State {
        return {
            ...config,
        };
    }

    public createControlsStateFromConfig(config: BooleanFilter_Config): BooleanFilter_ControlsState {
        return {
            identifier: config.identifier,
            type: config.type,
            applyFilter: false,
            values: {
                isChecked: false,
            },
        };
    }

    public updateControlsState(
        controlsState: BooleanFilter_ControlsState,
        state: BooleanFilter_State,
        payload: UpdateBooleanFilterPayload_ControlsState
    ): BooleanFilter_ControlsState {
        return {
            ...controlsState,
            applyFilter: payload.value.check,
            values: {
                ...controlsState.values,
                isChecked: payload.value.check,
            },
        };
    }

    public applyAggregation(
        state: BooleanFilter_State,
        controlsState: BooleanFilter_ControlsState,
        aggregation: BooleanFilterAggregation
    ): BooleanFilter_State {
        const newKeywords = state.values.keywords.map((keyword) => {
            const keywordAggregation = aggregation.find((a) => a.key === keyword.key);
            if (!keywordAggregation) {
                return { ...keyword, doc_count: 0 };
            }
            return {
                ...keyword,
                doc_count: keywordAggregation.doc_count,
            };
        });
        return {
            ...state,
            values: {
                keywords: newKeywords,
            },
        };
    }

    public cancelFilter(controlsState: BooleanFilter_ControlsState): BooleanFilter_ControlsState {
        return {
            ...controlsState,
            applyFilter: false,
            values: {
                ...controlsState.values,
                isChecked: false,
            },
        };
    }

    public createRequestPayloadFromState(state: BooleanFilter_ControlsState): O.Option<BooleanFilter_RequestParams> {
        if (state.applyFilter) {
            return O.some({
                name: state.identifier,
                checked: state.values.isChecked,
            });
        }
        return O.none;
    }

    public createSnapshotFromState(controlsState: BooleanFilter_ControlsState): O.Option<BooleanFilter_Snapshot> {
        if (controlsState.applyFilter) {
            return O.some(controlsState.identifier);
        }
        return O.none;
    }

    public applySnapshotToControlsState(
        controlsState: BooleanFilter_ControlsState,
        snapshot: BooleanFilter_Snapshot
    ): BooleanFilter_ControlsState {
        if (snapshot === controlsState.identifier) {
            return {
                ...controlsState,
                applyFilter: true,
                values: {
                    ...controlsState.values,
                    isChecked: true,
                },
            };
        }
        return controlsState;
    }
}
